import React, { useState } from 'react';
import Icon from '../AppIcon';
import Button from './Button';

const BulkActionBar = ({
  selectedCount = 0,
  totalCount = 0,
  actions = [],
  onSelectAll,
  onClearSelection,
  position = 'top',
  className = ''
}) => {
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  if (selectedCount === 0) return null;

  const handleActionClick = (action) => {
    if (action?.requiresConfirmation) {
      setPendingAction(action);
      setIsConfirmDialogOpen(true);
    } else {
      action?.onClick?.(selectedCount);
    }
  };

  const handleConfirmAction = () => {
    if (pendingAction) {
      pendingAction?.onClick?.(selectedCount);
      setPendingAction(null);
    }
    setIsConfirmDialogOpen(false);
  };

  const handleCancelAction = () => {
    setPendingAction(null);
    setIsConfirmDialogOpen(false);
  };

  const getActionVariant = (action) => {
    if (action?.variant) return action?.variant;
    if (action?.destructive) return 'destructive';
    if (action?.primary) return 'default';
    return 'outline';
  };

  return (
    <>
      <div className={`
        flex items-center justify-between p-4 bg-primary/5 border border-primary/20 rounded-lg
        ${position === 'sticky' ? 'sticky top-20 z-30' : ''}
        ${className}
      `}>
        {/* Selection Info */}
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <Icon name="CheckSquare" size={18} className="text-primary" />
            <span className="text-sm font-medium text-foreground">
              {selectedCount} of {totalCount} selected
            </span>
          </div>

          {/* Select All / Clear */}
          <div className="flex items-center space-x-2">
            {selectedCount < totalCount && onSelectAll && (
              <button
                onClick={onSelectAll}
                className="text-sm text-primary hover:text-primary/80 transition-smooth"
              >
                Select all {totalCount}
              </button>
            )}
            
            {onClearSelection && (
              <button
                onClick={onClearSelection}
                className="text-sm text-muted-foreground hover:text-foreground transition-smooth"
              >
                Clear selection
              </button>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center space-x-2">
          {actions?.map((action, index) => (
            <Button
              key={index}
              variant={getActionVariant(action)}
              size="sm"
              onClick={() => handleActionClick(action)}
              disabled={action?.disabled}
              iconName={action?.icon}
              iconPosition="left"
              className={action?.className}
            >
              {action?.label}
            </Button>
          ))}
        </div>
      </div>
      {/* Confirmation Dialog */}
      {isConfirmDialogOpen && pendingAction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-background/80 backdrop-blur-sm"
            onClick={handleCancelAction}
          />
          
          {/* Dialog */}
          <div className="relative bg-card border border-border rounded-lg shadow-moderate max-w-md w-full mx-4">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center space-x-3 mb-4">
                <div className={`
                  w-10 h-10 rounded-full flex items-center justify-center
                  ${pendingAction?.destructive ? 'bg-error/10' : 'bg-warning/10'}
                `}>
                  <Icon 
                    name={pendingAction?.destructive ? "AlertTriangle" : "HelpCircle"} 
                    size={20}
                    className={pendingAction?.destructive ? 'text-error' : 'text-warning'}
                  />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-foreground">
                    {pendingAction?.confirmTitle || 'Confirm Action'}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    This action affects {selectedCount} item{selectedCount !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>

              {/* Content */}
              <div className="mb-6">
                <p className="text-sm text-foreground">
                  {pendingAction?.confirmMessage || 
                   `Are you sure you want to ${pendingAction?.label?.toLowerCase()} ${selectedCount} item${selectedCount !== 1 ? 's' : ''}?`}
                </p>
                
                {pendingAction?.destructive && (
                  <div className="mt-3 p-3 bg-error/5 border border-error/20 rounded-md">
                    <p className="text-xs text-error">
                      <Icon name="AlertTriangle" size={14} className="inline mr-1" />
                      This action cannot be undone.
                    </p>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end space-x-3">
                <Button
                  variant="outline"
                  onClick={handleCancelAction}
                >
                  Cancel
                </Button>
                <Button
                  variant={pendingAction?.destructive ? 'destructive' : 'default'}
                  onClick={handleConfirmAction}
                  iconName={pendingAction?.icon}
                  iconPosition="left"
                >
                  {pendingAction?.confirmLabel || pendingAction?.label}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BulkActionBar;